/* ============================================================================
   Shared Ask Zana brain for the dock AND the full-screen tab.

   Both surfaces must behave as one conversation: same patches for live alert
   counts, same takeoverOffer chips, same auto-start on explicit handover.
   Keep response shaping here so the two UIs cannot drift again.
   ========================================================================== */

import type { AskResult } from './briefingsApi';
import type { ZanaIntent } from './zanaActions';
import type { ZanaChatMsg } from './zanaChatStore';
import {
  filterIntentsForLiveAlerts, intentNeedsConfirm, isExecutableIntent, isReadIntent,
} from './zanaActions';
/* Imported for use inside this module — the re-export further down makes the
   name available to importers, but does NOT bind it locally. It must also live
   up here: an import placed after an export trips eslint's import/first and
   fails the CRA build, even though tsc is perfectly happy with it. */
import { forbidsAlertClear, ownerAskedForAlertWork } from './zanaPatterns';

/* ── Saying yes out loud ──────────────────────────────────────────────────
   Confirmation was modelled purely as transient React state keyed by a chip's
   position in the DOM (`${messageIndex}-${intentIndex}`), so language could
   never reach it. Zana would arm an op, tell the owner to confirm, and then
   have no idea what "yes, confirm" meant — she would answer it as a brand new
   question, re-arm the same op, and invite the same "yes" again. That is the
   loop the stress test hit and could not break out of.

   She already SUGGESTS these words. They have to work.

   2026-09-17: and for five weeks they did not — both patterns carried a
   literal backspace character where \b was meant (97f44b0), so no typed
   yes or no ever matched. */
export const AFFIRM_RE = /^(y|ye|yes|yeah|yep|yup|ya|sure|ok|okay|k|go|go ahead|do it|just do it|please do|confirm|confirmed|confirm it|proceed|affirmative|sawa|ndio|ndiyo|fanya)\b[\s!.,'’]*$/i;

export const DENY_RE = /^(n|no|nope|nah|cancel|stop|don'?t|do not|abort|never ?mind|leave it|forget it|hapana)\b[\s!.,'’]*$/i;

/** Did the owner just say yes to something already on the table? */
export function isAffirmation(text: string): boolean {
  return AFFIRM_RE.test(String(text || '').trim());
}

/** Did they just say no? */
export function isDenial(text: string): boolean {
  return DENY_RE.test(String(text || '').trim());
}

/* ── Guide Mode interrupt ────────────────────────────────────────────────
   "stop" / "enough" / "later" always yields to chat. It lived as two copies
   of one regex in the dock and the full page; one copy now.

   2026-09-17: most of these words are ALSO denial words, and the interrupt
   check runs first — so whichever surface handles it must retire any op Zana
   is holding, or a later "ok" completes a write the owner just cancelled. */
const GUIDE_INTERRUPT_RE = /^(stop|enough|cancel|later|not now|leave it|never ?mind)[\s!.,'’]*$/i;

export function isGuideInterrupt(text: string): boolean {
  return GUIDE_INTERRUPT_RE.test(String(text || '').trim());
}

/* ── Unattended runs ─────────────────────────────────────────────────────
   2026-09-17: the server's `takeover.autoRun` only means "skip the chooser —
   the owner already consented". It is set for "open the high risk for me" and
   show_me asks as well as "just do it", so treating it as the chooser's
   "work through this myself" answer ran plain navigation asks unattended
   (850ms a line, read cards folded into a summary). Unattended now needs the
   owner's own words asking her to do the work — "just do it" and friends, or
   an order to work the alert queue (the office with twelve alerts at 6pm the
   chooser was written for). Lifecycle orders (suspend, ground, invite) and
   anything ambiguous get the guided walk — the watched mode. Writes stop and
   ask in both. */
const DO_IT_YOURSELF_RE = /\b(just do it|do it for me|do it yourself|work through (it|this|that|them|these|those)|handle (it|this|that|them)|take care of (it|this|that|them)|clear them)\b/i;
const CLEAR_QUEUE_RE = /\b(clear|triage)\s+(my|the|all|these|those)\b.{0,40}\b(alerts?|queue)\b/i;
const WANTS_WALKTHROUGH_RE = /\b(walk me|show me|guide me|teach me|step[- ]by[- ]step|how (do i|to))\b/i;
const REFUSES_HANDOVER_RE = /\b(don'?t|do not|never)\s+(just\s+)?(do it|handle|work through|take care|clear|triage)\b/i;

/* WHEN A WALK IS WHAT WAS ASKED FOR (Gabriel, 2026-09-18: "It should just take
   me straight to what i want … if you ask directly, it should just do it").
   Everything else goes straight to the thing — see composeJump in the engine.
   "show me X" is NOT a walk; "show me HOW" is. */
const WANTS_GUIDED_WALK_RE =
  /\b(walk me|take ?over|guide me|teach me|step[- ]by[- ]step|show me how|talk me through|how (do i|to))\b/i;

export function wantsGuidedWalk(question: string): boolean {
  return WANTS_GUIDED_WALK_RE.test(String(question || ''));
}

export function wantsUnattendedRun(question: string): boolean {
  const q = String(question || '');
  if (WANTS_WALKTHROUGH_RE.test(q) || REFUSES_HANDOVER_RE.test(q)) return false;
  if (DO_IT_YOURSELF_RE.test(q)) return true;
  return CLEAR_QUEUE_RE.test(q) && !forbidsAlertClear(q);
}

/**
 * Phrases in which Zana actually ASKS for a confirmation.
 *
 * The arming has to be earned by the sentence, not merely by a chip being
 * present. Nearly every fleet answer carries some write chip somewhere, so
 * arming on the chips alone meant a conversational "ok" after an unrelated
 * answer would execute an operation she had never offered to perform. A
 * spoken yes may only complete something she spoke a question about.
 */
const INVITES_CONFIRM_RE = /\b(shall i|should i\?|want me to|would you like me to|confirm|say (yes|"yes")|tap (below|the chip)|i'?ll (flag|park|clear|mark|close|email|send|draft|triage)|let me know)\b/i;

/* Both of these used to be defined here AND (differently) in zanaLocalAsk.ts.
   Single source of truth now lives in zanaPatterns.ts; re-exported so existing
   importers of this module keep working. */
export { forbidsAlertClear, ownerAskedForAlertWork } from './zanaPatterns';

/** The op a spoken "yes" should complete, if any.
 *
 *  2026-09-17: only when the answer holds exactly ONE write. This took the
 *  first, so an answer carrying two writes that asked "Want me to mark those
 *  alerts reviewed?" ran whichever chip happened to come first on a typed yes.
 *  A bare yes cannot say which it means; with two or more, the owner taps the
 *  chip they want. */
export function pendingConfirmable(
  intents: ZanaIntent[] | undefined,
  answerText?: string,
): ZanaIntent | null {
  if (!Array.isArray(intents)) return null;
  const ops = intents.filter((i) => intentNeedsConfirm(i) && isExecutableIntent(i));
  if (ops.length !== 1) return null;
  const op = ops[0];
  // No invitation in the words → no verbal confirm. The chip still works; it
  // just cannot be triggered by a stray "ok".
  if (!INVITES_CONFIRM_RE.test(String(answerText || ''))) return null;
  return op;
}

/* ── A held write, and when a typed yes may still complete it ─────────────
   2026-09-17: the held op used to be a bare intent in a ref. The executor runs
   it against whatever is live AT RUN TIME — mark_alerts_reviewed marks every
   unread alert that exists then, in the org that is open then — so a "yes"
   typed after a branch switch marked branch B's alerts off branch A's
   question; a "yes" in the dock answered a question the full page had asked
   since; and a "yes" during a takeover ran the write beside the panel's own
   Yes. A held write now remembers where it was asked, and a typed yes only
   completes it while all of that still holds. */
export type HeldConfirm = {
  intent: ZanaIntent;
  orgCode: string;
  /** Id of the Zana message that asked (ZanaChatMsg.id). */
  msgId: string;
  armedAt: number;
};

/** How long a typed yes may complete a held write: three minutes. Long enough
 *  to read a long answer and decide; short enough that the live queue she
 *  described (alerts arrive minute by minute) is still roughly what the write
 *  will act on. Past it the owner is asked again — never guessed for. */
export const HELD_CONFIRM_TTL_MS = 3 * 60_000;

/** What a yes or no gets when the write it would answer is no longer live.
 *  Said locally rather than sent to Ask: a bare "yes" reaching the engine
 *  spends an Ask and invites her to re-offer — re-arm — the very write that
 *  just went stale. This costs nothing and says plainly that nothing ran.
 *  It never names the op: it may belong to another branch or thread. */
export function staleHeldConfirmReply(affirmed: boolean): string {
  return affirmed
    ? "That confirmation is no longer live — nothing was changed. Ask me again and I'll set it up fresh."
    : 'Okay — nothing was changed.';
}

let zanaMsgSeq = 0;
/** Unique within the browser: dock and page share one stored thread. */
export function newZanaMsgId(now: number = Date.now()): string {
  zanaMsgSeq += 1;
  return `zm${now.toString(36)}${zanaMsgSeq.toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

/** Hold the write this answer invites a yes for, stamped with where it was asked. */
export function armHeldConfirm(
  intents: ZanaIntent[] | undefined,
  answerText: string | undefined,
  at: { orgCode: string | null; msgId: string; now: number },
): HeldConfirm | null {
  if (!at.orgCode) return null;
  const intent = pendingConfirmable(intents, answerText);
  return intent ? { intent, orgCode: at.orgCode, msgId: at.msgId, armedAt: at.now } : null;
}

/**
 * May a typed yes complete this held write right now?
 *
 * Same org; no takeover on screen (the panel owns its writes); within the TTL;
 * and the asking message is still the LAST message in this surface's thread —
 * stricter than "last Zana message" on purpose: a question typed since, on
 * either surface, supersedes the offer exactly as it does on the surface that
 * asked it.
 */
export function heldConfirmIsLive(
  held: HeldConfirm | null,
  ctx: { orgCode: string | null; chat: ZanaChatMsg[]; takeoverActive: boolean; now: number },
): boolean {
  if (!held) return false;
  if (!ctx.orgCode || held.orgCode !== ctx.orgCode) return false;
  if (ctx.takeoverActive) return false;
  const age = ctx.now - held.armedAt;
  if (!(age >= 0 && age <= HELD_CONFIRM_TTL_MS)) return false;
  const last = ctx.chat[ctx.chat.length - 1];
  return !!last && last.from === 'zana' && !!last.id && last.id === held.msgId;
}

/* ── A branch switch while something was loading ─────────────────────────
   2026-09-17: the console stays mounted across a branch switch, and so do the
   dock, the page and the takeover hook. An answer (or a walkthrough script)
   still loading when the owner switched landed in the NEW branch: appended to
   its thread with the old branch's write chips, saved there, and its takeover
   or auto-read started there — a Confirm on that chip marked the new branch's
   alerts off the old branch's question. Whatever was asked in one branch is
   dropped if it comes back in another, and she says so in one line. */
export const BRANCH_SWITCHED_REPLY = 'You switched branches before I finished — ask me again here.';

/**
 * May a chip on this message run right now?
 *
 * A typed yes gets heldConfirmIsLive; a chip is two deliberate taps on the op's
 * own name, so it skips the thread-position and time checks — but not these:
 *  - the message must have been answered in the org that is open now. Answers
 *    carry the org they were asked in; the executor acts on whichever org is
 *    open, so a chip from another branch would run against this one. Messages
 *    without a stamp (older threads, local lines) live in their org's own
 *    stored thread and are allowed.
 *  - no write while a takeover is on screen, in any phase: the panel asks its
 *    own Yes, and a chip beside it ran the same write twice (two messages to
 *    every late-bus parent).
 *
 * No three-minute limit, deliberately: that limit exists because a bare "yes"
 * names nothing. A chip names its op on the button and again on Confirm, the
 * executors re-read live data when they run (mark-reviewed marks what is
 * unread THEN and says how many), and chips in a restored thread going dead
 * after three minutes would push owners to spend an Ask to get them back.
 */
export function chipMayRun(
  msg: Pick<ZanaChatMsg, 'orgCode'>,
  needsConfirm: boolean,
  ctx: { orgCode: string | null; takeoverActive: boolean },
): boolean {
  if (msg.orgCode && msg.orgCode !== ctx.orgCode) return false;
  if (needsConfirm && ctx.takeoverActive) return false;
  return true;
}

/** Did the stored thread change under this surface — the other surface spoke,
 *  a notice landed, or the thread was swapped? Compared on the last message,
 *  which is what a surface's own save leaves identical. */
export function threadMovedUnder(current: ZanaChatMsg[], stored: ZanaChatMsg[]): boolean {
  const a = current[current.length - 1];
  const b = stored[stored.length - 1];
  if (!a || !b) return !!a !== !!b;
  return a.from !== b.from || a.text !== b.text || (a.id || '') !== (b.id || '');
}

export type ZanaTakeoverOffer = {
  topic: string;
  kind: 'do' | 'show';
  label: string;
  explainLabel?: string;
  prompt: string;
};

export type ShapedAsk = {
  text: string;
  intents: ZanaIntent[];
  offer?: ZanaTakeoverOffer;
  /** Explicit "take over…" — start driving immediately (ask WAS consent). */
  autoScriptId?: string;
  /** Ops params for lifecycle scripts (plate / driver) — never free-form steps. */
  autoOps?: Record<string, string> | null;
  /** Same consent, but a composed topic walk (Live Map / Accounts / …). */
  autoTopic?: string;
  /** Skip the Just do it / Walk me through chooser when the server set autoRun. */
  autoSkipChooser?: boolean;
  /** With autoSkipChooser: the owner asked her to do it herself — run
   *  unattended. False means the guided walk (see wantsUnattendedRun). */
  autoUnattended?: boolean;
  /** Auto-run first read intent (e.g. parent drop-off list). */
  autoRead?: ZanaIntent | null;
  mascotState?: string;
  /** Reasoning status carried through so the dock can render the resting pill.
   *  'limited' = resting (full depth returns on a clock); 'unavailable' = fault. */
  aiStatus?: 'unavailable' | 'limited';
  /** Present when resting — drives the reset countdown + "more credits" prompt. */
  limit?: AskResult['limit'];
};

/** Shape a /zana/ask response the same way on dock and full-screen. */
export function shapeAskResponse(
  q: string,
  res: AskResult,
  live: { unreadAlertCount: number; highRiskBusCount: number },
): ShapedAsk {
  const unread = Number(live.unreadAlertCount) || 0;
  const highRisk = Number(live.highRiskBusCount) || 0;
  const rawIntents = (res.ok && Array.isArray(res.intents) ? res.intents : []) as ZanaIntent[];
  const hadMark = rawIntents.some((i) => i && String(i.actionId) === 'mark_alerts_reviewed');
  const intents = filterIntentsForLiveAlerts(rawIntents, unread);

  /* An empty answer used to render as a literal "…" — a bubble with nothing in
     it, which reads as Zana having said something the eye cannot find. The
     takeover path already replaced it further down; every other path shipped
     the ellipsis. If there is genuinely nothing to show, say that. */
  const answerWasEmpty = res.ok && !String(res.answer || '').trim();
  let text = res.ok
    ? (res.answer || "That came back empty on my side — nothing was changed. Ask me again and I'll have another go.")
    : (res.message || "I couldn't answer that just now.");

  if (res.ok && hadMark && unread <= 0) {
    text = 'Live check: no unread alerts in the queue right now — nothing to mark.';
  }
  if (res.ok && highRisk > 0) {
    if (/no high-risk/i.test(text)) {
      text = `Your High Risk card is showing ${highRisk} right now`
        + (unread > 0
          ? ` (${unread} unread alert${unread === 1 ? '' : 's'}). `
          : ' (incidents and/or alerts). ')
        + 'Want me to open it and walk you through them?';
    } else if (/\b0 unread alert/i.test(text) || /unread alert\(s\) across\s*\./i.test(text)) {
      text = `Your High Risk card shows ${highRisk} right now`
        + (unread > 0
          ? ` — ${unread} unread alert${unread === 1 ? '' : 's'} in the queue. `
          : ' from incident clusters (the unread alert count can be 0 while this card is still lit). ')
        + 'Open High Risk Alerts to see which buses are involved.';
    }
  }

  let offer = (res.ok ? res.takeoverOffer : undefined) as ZanaTakeoverOffer | undefined;
  // Mentioning "alerts" in "don't clear alerts" / "missing refuels" must not
  // rewrite a fuel takeover into the High Risk queue. Only hijack when the
  // owner actually asked to work that queue.
  if (offer && ownerAskedForAlertWork(q) && highRisk > 0 && offer.topic !== 'alerts') {
    offer = {
      topic: 'alerts',
      kind: unread > 0 ? 'do' : 'show',
      label: unread > 0
        ? `Walk High Risk with me — clear my ${unread} alert${unread === 1 ? '' : 's'}`
        : 'Walk High Risk with me',
      explainLabel: offer.explainLabel || 'Explain it in chat',
      prompt: `Your High Risk card shows ${highRisk}. Stay in chat, or let me open High Risk and walk you through it — not the fuel tour.`,
    };
  }

  const autoScriptId = res.ok ? res.takeover?.scriptId : undefined;
  const autoOps = (res.ok && res.takeover && (res.takeover as { ops?: Record<string, string> }).ops)
    ? (res.takeover as { ops: Record<string, string> }).ops
    : null;
  const autoTopic = (!autoScriptId && res.ok) ? res.takeover?.topic : undefined;
  const autoSkipChooser = !!(res.ok && res.takeover?.autoRun);
  const autoUnattended = autoSkipChooser && wantsUnattendedRun(q);
  let autoRead: ZanaIntent | null = null;
  if (!autoScriptId && !autoTopic && res.ok) {
    const head = intents[0];
    // `!intentNeedsConfirm` matters for the CSV export: it is classified as a
    // read, so it qualified for auto-run and would have downloaded the file
    // the instant she mentioned it — while the answer beside it said "confirm
    // below". Anything that needs a tap must wait for the tap.
    if (head && isReadIntent(head) && isExecutableIntent(head) && !intentNeedsConfirm(head)) {
      autoRead = head;
    }
  }

  // A handover used to leave the thread blank. The server sends its narration
  // inside the script rather than as an `answer`, so `text` fell through to the
  // '…' placeholder above and the transcript showed the question with no reply
  // at all — scroll back afterwards and there is no record that she did
  // anything, let alone what. Leave a line behind.
  // A takeover speaks for itself, so its own line wins over the generic
  // empty-answer sentence above. `answerWasEmpty` carries that decision now
  // that the placeholder is a real sentence rather than an ellipsis.
  if ((autoScriptId || autoTopic) && (answerWasEmpty || !text.trim() || text.trim() === '…')) {
    text = 'Taking this one on screen — I\'ll walk it through below, and nothing changes until you confirm.';
  }

  return {
    text,
    intents,
    offer,
    autoScriptId,
    autoOps,
    autoTopic,
    autoSkipChooser,
    autoUnattended,
    autoRead,
    mascotState: res.mascotState,
    aiStatus: res.aiStatus,
    limit: res.limit,
  };
}

/* ── Resting footer ────────────────────────────────────────────────────────
 * One label for the dock and the full-screen page. It lived in the dock only,
 * so Ask Zana — the bigger surface — never told anyone why answers had gone
 * shallow or when depth returned.
 *
 * Four states, and the difference matters because two of them are OUR fault
 * rather than a budget: `window` and `daily` come back on a clock we can name,
 * `month` comes back on the 1st (never "tomorrow"), and `accounting` is a
 * spend-recording fault where promising any clock would be a lie. */
export type ZanaRestingLimit = NonNullable<ZanaChatMsg['restingLimit']>;

export function restingFooter(
  limit: ZanaRestingLimit,
  formatReset: (ms?: number) => string,
): { headline: string; canBuyCredits: boolean } {
  if (limit.reason === 'accounting') {
    return {
      headline: 'Deeper thinking is paused on our side · Trasse support has been alerted',
      canBuyCredits: false,
    };
  }
  if (limit.reason === 'month' || !limit.resetAtMs) {
    return {
      headline: 'This month\'s deep reasoning is used up · ask your Trasse admin to extend it',
      canBuyCredits: limit.upgrade !== false,
    };
  }
  const who = limit.scope === 'user'
    ? 'You\'ve used a lot of Zana\'s deep reasoning'
    : 'Zana\'s done a lot of deep thinking';
  const when = formatReset(limit.resetAtMs);
  return {
    headline: `${who} · full depth ${when === 'tomorrow' ? 'tomorrow' : `back ~${when}`}`,
    canBuyCredits: limit.upgrade !== false,
  };
}
